import { useState, useEffect, useCallback } from 'react';

// Breakpoint definitions matching Tailwind config
const BREAKPOINTS = {
  xs: 475,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

type Breakpoint = keyof typeof BREAKPOINTS;

/**
 * Hook for responsive design utilities
 */
export const useResponsive = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  const [currentBreakpoint, setCurrentBreakpoint] = useState<Breakpoint>('xs');

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      setWindowSize({ width, height });

      // Determine current breakpoint
      let breakpoint: Breakpoint = 'xs';
      for (const [bp, minWidth] of Object.entries(BREAKPOINTS)) {
        if (width >= minWidth) {
          breakpoint = bp as Breakpoint;
        }
      }
      setCurrentBreakpoint(breakpoint);
    };

    // Set initial values
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isBreakpoint = useCallback((breakpoint: Breakpoint) => {
    return windowSize.width >= BREAKPOINTS[breakpoint];
  }, [windowSize.width]);

  const isMobile = !isBreakpoint('lg'); // Mobile includes tablets for sidebar behavior
  const isSmallMobile = !isBreakpoint('sm');
  const isTablet = isBreakpoint('md') && !isBreakpoint('lg');
  const isDesktop = isBreakpoint('lg');
  const isLargeDesktop = isBreakpoint('xl');

  return {
    windowSize,
    currentBreakpoint,
    isBreakpoint,
    isMobile,
    isSmallMobile,
    isTablet,
    isDesktop,
    isLargeDesktop,
    breakpoints: BREAKPOINTS,
  };
};

/**
 * Hook for managing responsive navigation
 */
export const useResponsiveNavigation = () => {
  const { isMobile, isDesktop } = useResponsive();
  const [isNavigationOpen, setIsNavigationOpen] = useState(false);

  // Close navigation when switching from mobile to desktop
  // This ensures proper state reset when breakpoint changes
  useEffect(() => {
    if (isDesktop && isNavigationOpen) {
      setIsNavigationOpen(false);
    }
  }, [isDesktop, isNavigationOpen]);

  // Handle escape key and ensure proper cleanup
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isNavigationOpen && isMobile) {
        event.preventDefault();
        setIsNavigationOpen(false);
      }
    };

    // Only add listener when navigation is open and on mobile
    if (isNavigationOpen && isMobile) {
      document.addEventListener('keydown', handleKeyDown, { passive: false });
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isNavigationOpen, isMobile]);

  // Prevent body scroll when mobile navigation is open
  useEffect(() => {
    if (isMobile && isNavigationOpen) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [isMobile, isNavigationOpen]);

  const openNavigation = useCallback(() => {
    // Only allow opening on mobile
    if (isMobile) {
      setIsNavigationOpen(true);
    }
  }, [isMobile]);

  const closeNavigation = useCallback(() => {
    setIsNavigationOpen(false);
  }, []);

  const toggleNavigation = useCallback(() => {
    if (isMobile) {
      setIsNavigationOpen(prev => !prev);
    }
  }, [isMobile]);

  // Force close navigation if somehow it's open on desktop
  // This is a safety measure for state synchronization
  const safeIsNavigationOpen = isMobile ? isNavigationOpen : false;

  return {
    isNavigationOpen: safeIsNavigationOpen,
    openNavigation,
    closeNavigation,
    toggleNavigation,
    isMobile,
    isDesktop,
  };
};

/**
 * Hook for responsive grid layouts
 */
export const useResponsiveGrid = (options: {
  mobile?: number;
  tablet?: number;
  desktop?: number;
  largeDesktop?: number;
} = {}) => {
  const { isTablet, isDesktop, isLargeDesktop } = useResponsive();
  const { mobile = 1, tablet = 2, desktop = 3, largeDesktop = 4 } = options;

  let columns = mobile;
  if (isLargeDesktop) {
    columns = largeDesktop;
  } else if (isDesktop) {
    columns = desktop;
  } else if (isTablet) {
    columns = tablet;
  }

  const gridClasses = `grid grid-cols-${columns} gap-4`;
  
  return {
    columns,
    gridClasses,
  };
};

/**
 * Hook for responsive text sizing
 */
export const useResponsiveText = () => {
  const { currentBreakpoint } = useResponsive();

  const getTextSize = useCallback((sizes: {
    xs?: string;
    sm?: string;
    md?: string;
    lg?: string;
    xl?: string;
    '2xl'?: string;
  }) => {
    const breakpointOrder: Breakpoint[] = ['xs', 'sm', 'md', 'lg', 'xl', '2xl'];
    const currentIndex = breakpointOrder.indexOf(currentBreakpoint);
    
    // Find the largest applicable size
    for (let i = currentIndex; i >= 0; i--) {
      const bp = breakpointOrder[i];
      if (sizes[bp]) {
        return sizes[bp];
      }
    }
    
    return sizes.xs || 'text-base';
  }, [currentBreakpoint]);

  return { getTextSize };
};

/**
 * Hook for responsive spacing
 */
export const useResponsiveSpacing = () => {
  const { isMobile, isTablet } = useResponsive();

  const getSpacing = useCallback((config: {
    mobile: string;
    tablet?: string;
    desktop?: string;
  }) => {
    if (isMobile) return config.mobile;
    if (isTablet && config.tablet) return config.tablet;
    return config.desktop || config.tablet || config.mobile;
  }, [isMobile, isTablet]);

  const getPadding = useCallback((config: {
    mobile: string;
    tablet?: string;
    desktop?: string;
  }) => {
    const spacing = getSpacing(config);
    return `p-${spacing}`;
  }, [getSpacing]);

  const getMargin = useCallback((config: {
    mobile: string;
    tablet?: string;
    desktop?: string;
  }) => {
    const spacing = getSpacing(config);
    return `m-${spacing}`;
  }, [getSpacing]);

  return {
    getSpacing,
    getPadding,
    getMargin,
  };
};

/**
 * Hook for managing responsive modals and overlays
 */
export const useResponsiveModal = () => {
  const { isMobile } = useResponsive();

  const getModalClasses = useCallback(() => {
    if (isMobile) {
      return 'fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center';
    }
    return 'fixed inset-0 z-50 flex items-center justify-center p-4';
  }, [isMobile]);

  const getModalContentClasses = useCallback(() => {
    if (isMobile) {
      return 'w-full max-w-sm bg-white rounded-t-lg shadow-xl transform transition-all sm:max-w-lg sm:rounded-lg';
    }
    return 'w-full max-w-lg bg-white rounded-lg shadow-xl transform transition-all';
  }, [isMobile]);

  return {
    getModalClasses,
    getModalContentClasses,
    isMobile,
  };
};

/**
 * Hook for responsive table layouts
 */
export const useResponsiveTable = () => {
  const { isMobile, isTablet } = useResponsive();

  const shouldUseCardLayout = isMobile;
  const shouldUseScrollableTable = isTablet && !isMobile;
  const canUseFullTable = !isMobile && !isTablet;

  const getTableClasses = useCallback(() => {
    if (shouldUseCardLayout) {
      return 'space-y-4';
    }
    if (shouldUseScrollableTable) {
      return 'overflow-x-auto';
    }
    return 'overflow-hidden';
  }, [shouldUseCardLayout, shouldUseScrollableTable]);

  return {
    shouldUseCardLayout,
    shouldUseScrollableTable,
    canUseFullTable,
    getTableClasses,
    isMobile,
    isTablet,
  };
};